import type { ReactNode } from 'react';

type ProjectSectionShellProps = {
  eyebrow: string;
  title: string;
  description: string;
  icon: ReactNode;
  tone?: 'blue' | 'emerald' | 'amber' | 'violet';
  action?: ReactNode;
  children: ReactNode;
};

const toneClassMap = {
  blue: {
    hero: 'from-blue-600 via-blue-600 to-indigo-700 border-blue-100 shadow-blue-100',
    pill: 'bg-white/15 text-blue-50',
  },
  emerald: {
    hero: 'from-emerald-600 via-teal-600 to-cyan-700 border-emerald-100 shadow-emerald-100',
    pill: 'bg-white/15 text-emerald-50',
  },
  amber: {
    hero: 'from-amber-500 via-orange-500 to-red-500 border-amber-100 shadow-amber-100',
    pill: 'bg-white/15 text-amber-50',
  },
  violet: {
    hero: 'from-violet-600 via-fuchsia-600 to-blue-700 border-violet-100 shadow-violet-100',
    pill: 'bg-white/15 text-violet-50',
  },
};

const ProjectSectionShell = ({
  eyebrow,
  title,
  description,
  icon,
  tone = 'blue',
  action,
  children,
}: ProjectSectionShellProps) => {
  const toneClass = toneClassMap[tone];

  return (
    <div className="space-y-4 sm:space-y-6 animate-[fadeIn_0.4s_ease-out_forwards]">
      {/* Hero Header Section */}
      <section
        className={`rounded-2xl sm:rounded-3xl border bg-gradient-to-br p-4 sm:p-6 md:p-7 text-white shadow-lg ${toneClass.hero}`}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            {/* Eyebrow Pill */}
            <div
              className={`inline-flex max-w-full items-center gap-2 rounded-full px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-bold uppercase tracking-wider ${toneClass.pill}`}
            >
              {icon && <span className="shrink-0">{icon}</span>}
              <span className="truncate">{eyebrow}</span>
            </div>

            {/* Title */}
            <h2 className="mt-2.5 sm:mt-3 text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight break-words">
              {title}
            </h2>

            {/* Description */}
            {description && (
              <p className="mt-1.5 sm:mt-2 max-w-3xl text-xs sm:text-sm leading-relaxed text-white/85 break-words">
                {description}
              </p>
            )}
          </div>

          {/* Action Buttons Container */}
          {action && (
            <div className="w-full sm:w-auto shrink-0 flex flex-wrap items-center gap-2.5 mt-2 lg:mt-0">
              {action}
            </div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="rounded-2xl sm:rounded-3xl border border-slate-200 bg-white/90 p-4 sm:p-5 md:p-6 shadow-sm backdrop-blur">
        {children}
      </section>
    </div>
  );
};

export default ProjectSectionShell;
